/**
 * FoodDetailModal Component
 * Modal wrapper for displaying comprehensive food information
 */

import React from "react";
import { Food } from "../types/passio";
import { FoodDetailCard } from "./FoodDetailCard";

interface FoodDetailModalProps {
    isOpen: boolean;
    food: Food | any | null;
    onClose: () => void;
    variant?: "full" | "compact";
    loading?: boolean;
}

export const FoodDetailModal: React.FC<FoodDetailModalProps> = ({
    isOpen,
    food,
    onClose,
    variant = "full",
    loading = false,
}) => {
    if (!isOpen) return null;

    const overlayStyle: React.CSSProperties = {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "20px",
    };

    const modalStyle: React.CSSProperties = {
        maxWidth: variant === "compact" ? "500px" : "700px",
        width: "100%",
        maxHeight: "90vh",
        overflowY: "auto",
        boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        position: "relative",
    };

    const closeButtonStyle: React.CSSProperties = {
        position: "absolute",
        top: "16px",
        right: "16px",
        background: "rgba(255, 255, 255, 0.2)",
        border: "none",
        borderRadius: "50%",
        width: "32px",
        height: "32px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        color: "white",
        fontSize: "18px",
        fontWeight: "bold",
        zIndex: 1001,
        transition: "all 0.2s",
    };

    const loadingContainerStyle: React.CSSProperties = {
        backgroundColor: "white",
        borderRadius: "12px",
        padding: "48px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "200px",
        gap: "16px",
    };

    const spinnerStyle: React.CSSProperties = {
        width: "32px",
        height: "32px",
        border: "3px solid #f1f1f1",
        borderTop: "3px solid #3b82f6",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
    };

    const loadingTextStyle: React.CSSProperties = {
        fontSize: "16px",
        color: "#6b7280",
        fontWeight: "500",
    };

    return (
        <div style={overlayStyle} onClick={onClose}>
            <style>
                {`
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                `}
            </style>
            <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
                <button
                    onClick={onClose}
                    style={closeButtonStyle}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background =
                            "rgba(255, 255, 255, 0.3)";
                        e.currentTarget.style.transform = "scale(1.1)";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background =
                            "rgba(255, 255, 255, 0.2)";
                        e.currentTarget.style.transform = "scale(1)";
                    }}
                    title="Close"
                >
                    ×
                </button>
                {loading && !food ? (
                    <div style={loadingContainerStyle}>
                        <div style={spinnerStyle}></div>
                        <div style={loadingTextStyle}>
                            Loading ingredient details...
                        </div>
                    </div>
                ) : (
                    <FoodDetailCard food={food} variant={variant} />
                )}
            </div>
        </div>
    );
};
